import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { StatusBadge, ReasonPrompt } from "./shared";
import api, { formatEUR } from "../../lib/api";
import { toast } from "sonner";
import { ChevronRight, RefreshCw, Ban, CheckCircle2 } from "lucide-react";

export default function AdminPartners() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerId, setDrawerId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/partners");
      setItems(r.data.partners || []);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <AdminLayout title="Partners" subtitle="Embeds, revenue share, webhook delivery">
      <div className="xch-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="partners-table">
            <thead><tr className="bg-muted/50 border-b border-border">
              <Th>Name</Th><Th>ID</Th><Th right>Volume</Th><Th right>Orders</Th><Th right>Fee</Th><Th>Webhook</Th><Th>State</Th><Th>Created</Th><Th />
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center py-10 text-muted-foreground">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-muted-foreground">No partners registered.</td></tr>}
              {items.map((p) => (
                <tr key={p.id} className="border-b border-border hover:bg-muted/30 cursor-pointer" onClick={() => setDrawerId(p.id)} data-testid={`partner-row-${p.id}`}>
                  <td className="px-4 py-3 font-display font-bold text-foreground">{p.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.id.slice(0, 14)}{p.id.length > 14 ? "…" : ""}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono">{formatEUR(p.total_volume_eur)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono">{p.total_orders}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono">{p.fee_bps}bps</td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground truncate max-w-[200px]">{p.webhook_url || "—"}</td>
                  <td className="px-4 py-3">
                    {p.disabled ? <span className="text-xs font-semibold text-destructive">Disabled</span>
                                 : p.sandbox_mode ? <span className="text-xs font-semibold text-amber-600">Sandbox</span>
                                 : <span className="text-xs font-semibold text-emerald-700">Live</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums">{new Date(p.created_at).toLocaleDateString("en-IE")}</td>
                  <td className="px-2 py-3 text-muted-foreground"><ChevronRight className="w-4 h-4" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <PartnerDrawer partnerId={drawerId} onClose={() => setDrawerId(null)} onRefresh={load} />
    </AdminLayout>
  );
}

const Th = ({ children, right }) => (
  <th className={`px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground ${right ? "text-right" : "text-left"}`}>{children}</th>
);

function PartnerDrawer({ partnerId, onClose, onRefresh }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [confirmDisable, setConfirmDisable] = useState(false);

  useEffect(() => {
    if (!partnerId) return;
    api.get(`/admin/partners/${partnerId}`).then((r) => { setData(r.data); setEdits({}); });
  }, [partnerId]);

  const refresh = () => partnerId && api.get(`/admin/partners/${partnerId}`).then((r) => setData(r.data));

  const save = async () => {
    if (Object.keys(edits).length === 0) return;
    try {
      await api.patch(`/admin/partners/${partnerId}`, edits);
      toast.success("Saved");
      refresh(); onRefresh?.(); setEdits({});
    } catch { toast.error("Save failed"); }
  };
  const rotateSecret = async () => {
    if (!window.confirm("Rotate webhook secret? Existing will stop working immediately.")) return;
    try {
      const r = await api.post(`/admin/partners/${partnerId}/rotate-secret`);
      setRevealedSecret(r.data.webhook_secret);
      toast.success("Rotated — copy now");
      refresh();
    } catch { toast.error("Rotation failed"); }
  };
  const toggleDisable = async (reason) => {
    try {
      await api.patch(`/admin/partners/${partnerId}`, { disabled: !data.partner.disabled });
      toast.success(data.partner.disabled ? "Partner enabled" : "Partner disabled");
      refresh(); onRefresh?.();
    } catch { toast.error("Failed"); }
  };

  return (
    <Sheet open={!!partnerId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[560px] sm:max-w-none overflow-y-auto" data-testid="partner-drawer">
        <SheetHeader><SheetTitle className="font-display text-xl">{data?.partner?.name || "Partner"}</SheetTitle></SheetHeader>
        {!data ? <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div> : (
          <div className="mt-4 space-y-4">
            <div className="bg-muted/40 rounded-2xl p-3 text-xs space-y-0.5">
              <div className="font-mono text-foreground">{data.partner.id}</div>
              <div className="font-mono text-muted-foreground">API Key: {data.partner.api_key}</div>
              <div className="font-mono text-muted-foreground">Webhook Secret: {revealedSecret || data.partner.webhook_secret}</div>
              <div className="font-mono text-muted-foreground">URL: {data.partner.webhook_url || "—"}</div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <FieldNum label="Fee (bps)" value={edits.fee_bps ?? data.partner.fee_bps} onChange={(v) => setEdits({ ...edits, fee_bps: Number(v) })} testid="edit-fee" />
              <FieldNum label="Min EUR" value={edits.min_amount_eur ?? data.partner.min_amount_eur ?? ""} onChange={(v) => setEdits({ ...edits, min_amount_eur: v === "" ? null : Number(v) })} testid="edit-min" />
              <FieldNum label="Max EUR" value={edits.max_amount_eur ?? data.partner.max_amount_eur ?? ""} onChange={(v) => setEdits({ ...edits, max_amount_eur: v === "" ? null : Number(v) })} testid="edit-max" />
            </div>

            <div className="flex gap-2">
              <Button onClick={save} disabled={Object.keys(edits).length === 0} className="bg-primary hover:bg-primary/90 text-white rounded-xl flex-1" size="sm" data-testid="partner-save-btn">Save changes</Button>
              <Button onClick={rotateSecret} variant="outline" className="rounded-xl gap-1" size="sm" data-testid="partner-rotate-btn"><RefreshCw className="w-3.5 h-3.5" /> Rotate secret</Button>
              <Button onClick={toggleDisable} variant="outline" className={`rounded-xl gap-1 ${data.partner.disabled ? "" : "text-destructive border-destructive/30"}`} size="sm" data-testid="partner-disable-btn">
                {data.partner.disabled ? <><CheckCircle2 className="w-3.5 h-3.5" /> Enable</> : <><Ban className="w-3.5 h-3.5" /> Disable</>}
              </Button>
            </div>

            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Recent webhooks ({data.webhook_logs.length})</div>
              <div className="bg-white border border-border rounded-2xl divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {data.webhook_logs.length === 0 ? <div className="text-sm text-muted-foreground p-3 text-center">No webhook attempts.</div>
                  : data.webhook_logs.map((w) => (
                    <div key={w.id} className="p-2.5 text-xs flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5"><span className="font-mono">{w.event || "—"}</span><StatusBadge status={w.status} /></div>
                        <div className="text-muted-foreground font-mono text-[10px]">{new Date(w.last_attempt_at).toLocaleString("en-IE")} · HTTP {w.last_status || "—"} · {w.latency_ms || "?"}ms</div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

const FieldNum = ({ label, value, onChange, testid }) => (
  <div className="space-y-1">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
    <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="rounded-xl font-mono" data-testid={testid} />
  </div>
);
